import { IconButton, OutlineButton } from "components/button";
import { ChecklistExistingPicker } from "components/checklist_dropdown";
import { Input } from "components/input";
import * as React from "react";
import { useRef, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DraggableProvidedDragHandleProps,
} from "react-beautiful-dnd";
import { CgUndo } from "react-icons/cg";
import { MdDragIndicator } from "react-icons/md";
import {
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiCloseCircleFill,
  RiDeleteBin2Fill,
} from "react-icons/ri";
import { useTranslation } from "react-i18next";
import type { ChecklistsApi, ChecklistRow } from "./useChecklistRows";

type Props = {
  /** The hook's return — owns state, queries, and `flushPending`. */
  checklists: ChecklistsApi;
  /** Raw form value, passed straight from the modal's `formik.values.fk_appointment_type_id`. */
  fkAppointmentTypeId: string;
  /** Effective type id (>0 finite) or null. */
  actualTypeOrNull: number | null;
  /** Effective default id, or null for parent. */
  defaultNum: number | null;
  /** Dropdown mode — drives the "(No auto-fill)" hint visibility. */
  selectedDefaultMode?: string | null;
  /** True when the modal shows the sub-type / default Field. */
  hasSubAppointmentDefaultPicker?: boolean;
  isCompleted: boolean;
};

/**
 * Pure presenter for the appointment-modal checklist panel. State, queries,
 * mutations and `flushPending` all live in `useChecklistRows` (called by the
 * parent). This file owns only ephemeral UI state — the picker open flag and
 * the panel collapsed/expanded flag.
 */
export function AppointmentModalChecklistsSection({
  checklists,
  fkAppointmentTypeId,
  actualTypeOrNull,
  defaultNum,
  selectedDefaultMode = null,
  hasSubAppointmentDefaultPicker = true,
  isCompleted,
}: Props) {
  const { t } = useTranslation();
  const tBase = "containers.appointmentCard.appointment-folder.appointmentModal";
  const tr = React.useCallback(
    (key: string, options?: Record<string, string | number>) =>
      t(`${tBase}.${key}`, options),
    [t]
  );

  const [checklistRowsExpanded, setChecklistRowsExpanded] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const templatePickerAnchorRef = useRef<HTMLDivElement | null>(null);

  const {
    displayRows,
    scopedTemplatesNotYetAdded,
    isLoading,
    isError,
    templatesIsLoading,
    handleLabelChange,
    handleRequiredChange,
    handleDismissRow,
    handleUndoPatchesForRow,
    handleConfirmTemplatesFromPicker,
    handleReorder,
  } = checklists;

  const typeNum = Number(fkAppointmentTypeId);
  const readOnly = isCompleted || (actualTypeOrNull != null && isLoading);

  const handleDragEnd = (result: {
    destination?: { index: number } | null;
    source: { index: number };
  }) => {
    if (!result.destination) return;
    handleReorder(result.source.index, result.destination.index);
  };

  const droppableId = `appt-modal-checklists-${actualTypeOrNull ?? "na"}-${defaultNum ?? "parent"}`;

  const renderRow = (
    row: ChecklistRow,
    opts?: {
      dragHandleProps?: DraggableProvidedDragHandleProps;
      isDragging?: boolean;
    }
  ) => {
    /** Anything not yet persisted — cancel removes the row locally. */
    const isPersistedAppointmentEntry = row.entry_id != null;
    const trimLabel = (s: string) => s.trim();
    const labelsDiffer =
      trimLabel(row.label ?? "") !== trimLabel(row.originalLabel ?? "");
    const requiredDiffer = row.required !== row.originalRequired;
    const orderDirty =
      Number.isFinite(row.originalOrder ?? NaN) && row.order !== row.originalOrder;
    const showSavedUndo =
      isPersistedAppointmentEntry &&
      (labelsDiffer || requiredDiffer || orderDirty);
    const showCancelPendingRow = !isPersistedAppointmentEntry;

    return (
      <div
        className={`flex flex-wrap items-center gap-2 border-b border-gray-100 pb-2 last:border-0${opts?.isDragging ? " bg-gray-50 rounded" : ""}`}
      >
        {opts?.dragHandleProps ? (
          <button
            type="button"
            className="flex shrink-0 items-center self-center p-0.5 text-gray-500 hover:text-gray-800 cursor-grab rounded border-0 bg-transparent"
            aria-label={tr("Checklists drag to reorder")}
            {...opts.dragHandleProps}
          >
            <MdDragIndicator size={22} aria-hidden />
          </button>
        ) : null}
        <Input
          value={row.label ?? ""}
          onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
            handleLabelChange(row, ev.target.value)
          }
          disabled={readOnly}
          style={{ flex: "1 1 200px", minWidth: 0 }}
        />
        <label className="flex items-center gap-1 text-sm whitespace-nowrap">
          <input
            type="checkbox"
            checked={row.required === true}
            onChange={(ev) => handleRequiredChange(row, ev.target.checked)}
            disabled={readOnly}
          />
          {tr("Checklists required")}
        </label>
        {!readOnly ? (
          <IconButton
            type="button"
            onClick={() => {
              if (showSavedUndo) {
                handleUndoPatchesForRow(row);
                return;
              }
              handleDismissRow(row);
            }}
            aria-label={
              showCancelPendingRow
                ? tr("Checklists dismiss pending row")
                : showSavedUndo
                  ? tr("Checklists undo row changes")
                  : tr("Checklists remove")
            }
            title={
              showCancelPendingRow
                ? tr("Checklists dismiss pending row")
                : showSavedUndo
                  ? tr("Checklists undo row changes")
                  : tr("Checklists remove")
            }
            style={
              showCancelPendingRow
                ? { color: "#6b7280" }
                : showSavedUndo
                  ? undefined
                  : { color: "#dc2626" }
            }
            disabled={isLoading}
          >
            {showCancelPendingRow ? (
              <RiCloseCircleFill size={22} />
            ) : showSavedUndo ? (
              <CgUndo size={24} className="text-blue-500" />
            ) : (
              <RiDeleteBin2Fill size={20} />
            )}
          </IconButton>
        ) : null}
      </div>
    );
  };

  return (
    <div className="mt-1 mb-2 px-1">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h2 className="text-lg font-semibold m-0">{tr("Checklists on appointment")}</h2>
      </div>
      {readOnly && isCompleted ? (
        <p className="text-sm text-gray-600 mb-2">{tr("Checklists read-only completed")}</p>
      ) : null}
      {!Number.isFinite(typeNum) || fkAppointmentTypeId === "" ? (
        <p className="text-sm text-gray-600">{tr("Checklists pick type hint")}</p>
      ) : null}
      {!readOnly &&
      hasSubAppointmentDefaultPicker &&
      selectedDefaultMode === "new" &&
      Number.isFinite(typeNum) &&
      scopedTemplatesNotYetAdded.length > 0 &&
      !templatesIsLoading ? (
        <p className="text-sm text-blue-800 bg-blue-50 border border-blue-100 rounded-md px-2 py-2 mb-2">
          {tr("Checklists no autofill templates hint", {
            count: scopedTemplatesNotYetAdded.length,
          })}
        </p>
      ) : null}
      {isError ? (
        <p className="text-sm text-red-600">{tr("Checklists load error")}</p>
      ) : null}
      <div className="border border-gray-200 rounded-md">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 px-2 py-2 text-left text-sm font-medium text-gray-800 bg-gray-50 hover:bg-gray-100 border-b border-gray-200"
          onClick={() => setChecklistRowsExpanded((v) => !v)}
          aria-expanded={checklistRowsExpanded}
        >
          <span className="flex min-w-0 items-center gap-2">
            {checklistRowsExpanded ? (
              <RiArrowDownSLine className="shrink-0" size={18} aria-hidden />
            ) : (
              <RiArrowRightSLine className="shrink-0" size={18} aria-hidden />
            )}
            <span className="truncate">
              {tr("Checklists rows panel")}
              {!isLoading ? ` (${displayRows.length})` : ""}
            </span>
          </span>
        </button>
        {checklistRowsExpanded ? (
          <div className="flex flex-col gap-2 p-2">
            {displayRows.length === 0 && !isLoading ? (
              <p className="text-sm text-gray-500">{tr("Checklists none")}</p>
            ) : null}
            {readOnly ? (
              displayRows.map((row: ChecklistRow) => (
                <React.Fragment key={row.key}>{renderRow(row)}</React.Fragment>
              ))
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId={droppableId}>
                  {(droppableProvided) => (
                    <div
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                      className="flex flex-col gap-2"
                    >
                      {displayRows.map((row: ChecklistRow, index: number) => (
                        <Draggable key={row.key} draggableId={row.key} index={index}>
                          {(draggableProvided, snapshot) => (
                            <div
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              style={{
                                ...draggableProvided.draggableProps.style,
                              }}
                            >
                              {renderRow(row, {
                                dragHandleProps: draggableProvided.dragHandleProps,
                                isDragging: snapshot.isDragging,
                              })}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        ) : null}
      </div>
      {!readOnly && Number.isFinite(typeNum) ? (
        <div className="mt-2 relative" ref={templatePickerAnchorRef}>
          <OutlineButton type="button" onClick={() => setTemplatePickerOpen((o) => !o)}>
            {tr("Checklists add existing")}
          </OutlineButton>
          <ChecklistExistingPicker
            open={templatePickerOpen}
            onOpenChange={setTemplatePickerOpen}
            anchorRef={templatePickerAnchorRef}
            appointmentTypeId={typeNum}
            subAppointmentTypeId={defaultNum}
            variant="appointment"
            onConfirm={handleConfirmTemplatesFromPicker}
          />
        </div>
      ) : null}
    </div>
  );
}
